import { $config } from '@client/core/atoms/config.js';
import {
  getPlacementList,
  $placementsMap,
  getPlacementById
} from '@client/core/atoms/placements.js';
import {
  fetchAllElements,
  fetchElement,
  fetchElementById,
  getDocumentHeight,
  querySelect,
  styleElements
} from '@client/core/utils/dom/domUtils.js';
import { listenKeys } from '@podium/store';
import {
  AdPlacement,
  Brand,
  type PlacementId
} from '@schibsted-nmp/advertising-shared';

const BANNERS_STICKY_CONTAINER_CLASS_NAME_QUERY = '.banners__sticky-container';
const FOOTER_ELEM_QUERIES = ['finn-footer', 'mfp-make-model-seo-links'];
const offset = 100;
const FOOTER_HEIGHT_PX = 500;
const activeAdnamiPlacementIds = new Array<PlacementId>();
const DBA_MASTER_COMPANION_TOP_HEIGHT = 180;
const DBA_MASTER_COMPANION_WALLPAPER_HEIGHT = 1080;
const BLOCKET_TOP1_WALLPAPER_HEIGHT = 1080;
const FIRST_ROW_SKYSCRAPER_CONTAINER_HEIGHT = 780;
const SECOND_ROW_SKYSCRAPER_MARGIN_TOP = 20;

let bannerStickyContainers: NodeListOf<HTMLElement> | null = null;
let top1WallpaperIsActive = false;

function fetchStickyContainer() {
  if (!bannerStickyContainers) {
    bannerStickyContainers = fetchAllElements(
      BANNERS_STICKY_CONTAINER_CLASS_NAME_QUERY
    );
  }
}

const getPlacementElementFromNodesList = (
  nodeList: NodeListOf<HTMLElement>,
  placementId: PlacementId
): undefined | HTMLElement => {
  for (const currentNode of nodeList) {
    const placementElement = currentNode.querySelector(
      `[placementId="${placementId}"]`
    );

    if (placementElement) return currentNode;
  }

  return undefined;
};

const applyStylesToContainer = (
  container: HTMLElement | undefined,
  isSecondRowEmpty: boolean,
  styles: {
    whenEmpty: Record<string, string>;
    whenNotEmpty: Record<string, string>;
  }
) => {
  if (!container) return;

  styleElements(
    container,
    isSecondRowEmpty ? styles.whenEmpty : styles.whenNotEmpty
  );
};

const isPlacementEmpty = (placementId: PlacementId) => {
  const placement = getPlacementById(placementId);

  return !placement || placement.status === 'error';
};

function setSkyscrapersStickyHeight() {
  const { brand, placements } = $config.get() ?? {};

  const hasTwoSkyscraperRows =
    placements?.some(
      (placement) =>
        placement.placementId === AdPlacement.AdvtRight2.id ||
        placement.placementId === AdPlacement.AdvtLeft2.id
    ) ?? false;

  const firstRowStickyContainers = fetchAllElements('.banners__sticky--1');
  const secondRowStickyContainers = fetchAllElements('.banners__sticky--2');

  const advtRight1Element = getPlacementElementFromNodesList(
    firstRowStickyContainers,
    AdPlacement.AdvtRight1.id
  );

  const advtRight2Element = getPlacementElementFromNodesList(
    secondRowStickyContainers,
    AdPlacement.AdvtRight2.id
  );

  const advtLeft1Element = getPlacementElementFromNodesList(
    firstRowStickyContainers,
    AdPlacement.AdvtLeft1.id
  );

  const advtLeft2Element = getPlacementElementFromNodesList(
    secondRowStickyContainers,
    AdPlacement.AdvtLeft2.id
  );

  const isDbaMasterCompanionAd =
    brand === Brand.Dba && fetchElement('#advt_wallpaper iframe');

  const isAdvtRight2Empty = isPlacementEmpty(AdPlacement.AdvtRight2.id);
  const isAdvtLeft2Empty = isPlacementEmpty(AdPlacement.AdvtLeft2.id);

  if (isDbaMasterCompanionAd) {
    const skyscraperAds = fetchAllElements(
      '.banners__sticky--1 advt-component'
    );

    styleElements(skyscraperAds, {
      marginTop: `${DBA_MASTER_COMPANION_TOP_HEIGHT}px`
    });

    styleElements(firstRowStickyContainers, {
      height: `calc(${DBA_MASTER_COMPANION_WALLPAPER_HEIGHT}px - ${DBA_MASTER_COMPANION_TOP_HEIGHT}px)`
    });
  } else if (hasTwoSkyscraperRows) {
    const styles = {
      whenEmpty: { height: '100%' },
      whenNotEmpty: {
        height: `${FIRST_ROW_SKYSCRAPER_CONTAINER_HEIGHT}px`
      }
    };

    applyStylesToContainer(advtLeft1Element, isAdvtLeft2Empty, styles);
    applyStylesToContainer(advtRight1Element, isAdvtRight2Empty, styles);
  } else {
    styleElements(firstRowStickyContainers, { height: '100%' });
  }

  if (hasTwoSkyscraperRows) {
    const secondRowSkyscraperOffset =
      SECOND_ROW_SKYSCRAPER_MARGIN_TOP +
      (isDbaMasterCompanionAd
        ? DBA_MASTER_COMPANION_WALLPAPER_HEIGHT
        : FIRST_ROW_SKYSCRAPER_CONTAINER_HEIGHT);

    const styles = {
      whenEmpty: { height: '0px' },
      whenNotEmpty: {
        height: `calc(100% - ${secondRowSkyscraperOffset}px)`,
        marginTop: `${secondRowSkyscraperOffset}px`
      }
    };

    applyStylesToContainer(advtLeft2Element, isAdvtLeft2Empty, styles);
    applyStylesToContainer(advtRight2Element, isAdvtRight2Empty, styles);
  } else {
    styleElements(secondRowStickyContainers, { height: '0px' });
  }

  // push skyscrapers down if top1-wallpaper is active
  if (brand === Brand.Blocket && top1WallpaperIsActive) {
    const secondRowSkyscraperOffset =
      BLOCKET_TOP1_WALLPAPER_HEIGHT + SECOND_ROW_SKYSCRAPER_MARGIN_TOP;

    styleElements(firstRowStickyContainers, {
      marginTop: `${secondRowSkyscraperOffset}px`
    });
    if (hasTwoSkyscraperRows) {
      const doubleX = secondRowSkyscraperOffset * 2;

      const styles = {
        whenEmpty: {
          height: '0px',
          marginTop: `${doubleX}px`
        },
        whenNotEmpty: {
          height: `calc(100% - ${doubleX}px)`,
          marginTop: `${doubleX}px`
        }
      };

      applyStylesToContainer(advtLeft2Element, isAdvtLeft2Empty, styles);
      applyStylesToContainer(advtRight2Element, isAdvtRight2Empty, styles);
    }
  }
}

function setSkyscraperContainerHeight() {
  setSkyscrapersStickyHeight();
  const collidingElements = activeAdnamiPlacementIds
    .map((placementId) => fetchElementById(placementId))
    .filter((placement) => placement !== null);

  const footerElements = FOOTER_ELEM_QUERIES.map((query) =>
    querySelect(query)
  ).filter((element) => element !== null);
  collidingElements.push(...footerElements);

  const [firstCollisionElement] = collidingElements.sort(
    (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
  );

  if (firstCollisionElement) {
    setBannerStickyContainerHeight(
      firstCollisionElement.getBoundingClientRect().top +
        window.scrollY -
        offset
    );
  } else {
    // fallback. eg. if footer element was not found
    setBannerStickyContainerHeight(
      getDocumentHeight() - FOOTER_HEIGHT_PX - offset
    );
  }
}

function setBannerStickyContainerHeight(newHeight: number) {
  fetchStickyContainer();
  if (!bannerStickyContainers) return;

  for (const container of bannerStickyContainers) {
    container.style.height = `${newHeight}px`;
  }
}

export function setAdnamiPlacement(placementId: PlacementId) {
  if (
    !activeAdnamiPlacementIds.some((placements) => placements === placementId)
  ) {
    activeAdnamiPlacementIds.push(placementId);
    setSkyscraperContainerHeight();
  }
}

export function initiateHandleStickySkyscrapers() {
  if (typeof ResizeObserver !== 'undefined') {
    let prevBodyHeight = document.body.offsetHeight;

    // Create and setup ResizeObserver to watch body height changes
    const resizeObserver = new ResizeObserver(() => {
      const currentBodyHeight = document.body.offsetHeight;

      if (currentBodyHeight !== prevBodyHeight) {
        prevBodyHeight = currentBodyHeight;
        setSkyscraperContainerHeight();
      }
    });

    // Start observing the document body
    resizeObserver.observe(document.body);
  }

  for (const placement of getPlacementList()) {
    const id = placement.placementId;
    // add listener on each placements status and creative type changes
    listenKeys(
      $placementsMap,
      [`${id}.status`, `${id}.creativeType`],
      (placements, oldPlacements) => {
        // check if placement exists
        const p = placements[id];
        if (!p) return;

        // check for changes
        const old = oldPlacements[id];
        const statusChanged = p.status !== old?.status;
        const creativeChanged = p.creativeType !== old?.creativeType;

        // bail early if nothing changed
        if (!statusChanged && !creativeChanged) return;
        if (statusChanged) {
          if (p.status === 'loaded' && old?.status !== 'loaded') {
            setSkyscraperContainerHeight();
          }
        }

        // if creative changed and its top1 placement -> check if wallpaper was added/removed
        if (creativeChanged && id === AdPlacement.AdvtTop1.id) {
          const next = p.creativeType ?? '';
          const prev = old?.creativeType ?? '';
          if (next === 'top1-wallpaper' && prev !== 'top1-wallpaper') {
            setTop1Wallpaper();
          } else if (prev === 'top1-wallpaper' && next !== 'top1-wallpaper') {
            clearTop1Wallpaper();
          }
        }
      }
    );
  }
}

function setTop1Wallpaper() {
  if (!top1WallpaperIsActive) {
    top1WallpaperIsActive = true;
    setSkyscraperContainerHeight();
  }
}

function clearTop1Wallpaper() {
  if (top1WallpaperIsActive) {
    top1WallpaperIsActive = false;
    setSkyscraperContainerHeight();
  }
}
